import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Clapperboard, Sparkles, Mic, Play, Download, CheckCircle2, XCircle, Upload, User, GitCompare } from "lucide-react";
import { sr } from "../../lib/sr";
import RefineChat from "../../components/smartreach/RefineChat";

const DEFAULT_SCRIPT =
  "Craving something great nearby? Come see why locals love us. This week only, enjoy a special welcome offer when you stop by. We can't wait to see you!";

export default function VideoStudio() {
  const [opts, setOpts] = useState(null);
  const [script, setScript] = useState(DEFAULT_SCRIPT);
  const [voiceId, setVoiceId] = useState("");
  const [avatarId, setAvatarId] = useState("");
  const [mode, setMode] = useState("avatar"); // 'avatar' | 'photo'
  const [photo, setPhoto] = useState(null); // {talking_photo_id, preview_url}
  const [uploading, setUploading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [presenters, setPresenters] = useState([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null); // current processing/complete video
  const [refine, setRefine] = useState(null); // {video, thread, busy}
  const [compare, setCompare] = useState(null); // {refined, original}
  const pollRef = useRef(null);
  const audioRef = useRef(null);

  const loadVideos = () => sr.listVideos().then((r) => setVideos(r.videos || [])).catch(() => {});
  const loadPresenters = () => sr.listPresenters().then((r) => setPresenters(r.presenters || [])).catch(() => {});

  useEffect(() => {
    sr.videoOptions()
      .then((o) => { setOpts(o); setVoiceId(o.voices?.[0]?.voice_id || ""); setAvatarId(o.avatars?.[0]?.avatar_id || ""); })
      .catch(() => setOpts({ avatars: [], voices: [], error: true }));
    loadVideos();
    loadPresenters();
    return () => clearInterval(pollRef.current);
  }, []);

  const poll = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await sr.videoStatus(id);
        setActive(r.video);
        if (r.video.status === "completed" || r.video.status === "failed") {
          clearInterval(pollRef.current);
          loadVideos();
          if (r.video.status === "completed") toast.success("Your video ad is ready!");
          if (r.video.status === "failed") toast.error("Rendering failed — try a different presenter or shorter script");
        }
      } catch { /* keep polling */ }
    }, 8000);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await sr.uploadVideoPhoto(file);
      setPhoto({ talking_photo_id: r.talking_photo_id, preview_url: r.preview_url });
      loadPresenters();
      toast.success("Photo ready — it will become your talking presenter");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not use that photo");
    } finally { setUploading(false); }
  };

  const removePresenter = async (id) => {
    try { await sr.deletePresenter(id); loadPresenters(); } catch { toast.error("Could not remove"); }
  };

  const generate = async () => {
    if (script.trim().length < 10) return toast.error("Write a slightly longer script");
    if (!voiceId) return toast.error("Pick a voice");
    if (mode === "avatar" && !avatarId) return toast.error("Pick a presenter");
    if (mode === "photo" && !photo) return toast.error("Upload a photo first");
    setBusy(true);
    setActive(null);
    try {
      const payload = mode === "photo"
        ? { script: script.trim(), voice_id: voiceId, talking_photo_id: photo.talking_photo_id }
        : { script: script.trim(), voice_id: voiceId, avatar_id: avatarId };
      const r = await sr.generateVideo(payload);
      setActive(r.video);
      poll(r.video.id);
      toast.success("Generating voiceover + video… this takes about a minute");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start generation");
    } finally {
      setBusy(false);
    }
  };

  const playVoice = (url) => { if (!url) return; if (audioRef.current) audioRef.current.pause(); audioRef.current = new Audio(url); audioRef.current.play(); };

  const sendVideoRefine = async (text) => {
    setRefine((r) => ({ ...r, busy: true, thread: [...r.thread, { role: "user", text }] }));
    try {
      const res = await sr.refineVideo(refine.video.id, text);
      if (res.ok) {
        setRefine((r) => ({ ...r, busy: false, video: res.video,
          thread: [...r.thread, { role: "assistant", text: res.reply || "Re-rendering with your changes." }] }));
        setActive(res.video);
        poll(res.video.id);
        toast.success("Re-rendering your video with the changes…");
      } else {
        setRefine((r) => ({ ...r, busy: false,
          thread: [...r.thread, { role: "assistant", text: res.reply || "Couldn't apply that — try rephrasing." }] }));
      }
    } catch (e) {
      setRefine((r) => ({ ...r, busy: false }));
      toast.error(e?.response?.data?.detail || "Refine failed");
    }
  };
  const openRefine = (v) => setRefine({ video: v, thread: v.chat || [], busy: false });

  if (!opts) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;

  if (opts.error || (opts.avatars.length === 0)) {
    return (
      <div>
        <Head />
        <div className="sr-card" data-testid="video-unavailable"><p className="sr-muted">The video studio is temporarily unavailable. Please try again shortly.</p></div>
      </div>
    );
  }

  return (
    <div data-testid="video-studio">
      <Head />

      <div className="sr-card">
        <div className="sr-field">
          <label className="sr-label">Script</label>
          <textarea className="sr-input" rows={4} value={script} onChange={(e) => setScript(e.target.value)}
            placeholder="Write what your presenter should say…" data-testid="video-script" style={{ resize: "vertical" }} />
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>Tip: paste a script from your AI Campaign Builder for a matching commercial.</p>
        </div>

        <div className="sr-field" style={{ marginTop: 14 }}>
          <label className="sr-label"><Mic size={13} style={{ verticalAlign: -2 }} /> Voice</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {opts.voices.map((v) => (
              <button key={v.voice_id} onClick={() => setVoiceId(v.voice_id)} data-testid={`voice-${v.voice_id}`}
                className={`sr-btn sr-btn-sm ${voiceId === v.voice_id ? "sr-btn-primary" : "sr-btn-ghost"}`}>
                {v.name?.split(" - ")[0] || "Voice"}
                {v.preview && <Play size={12} onClick={(e) => { e.stopPropagation(); playVoice(v.preview); }} style={{ marginLeft: 6 }} />}
              </button>
            ))}
          </div>
        </div>

        <div className="sr-field" style={{ marginTop: 14 }}>
          <label className="sr-label"><Sparkles size={13} style={{ verticalAlign: -2 }} /> Presenter</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button className={`sr-btn sr-btn-sm ${mode === "avatar" ? "sr-btn-primary" : "sr-btn-ghost"}`}
              onClick={() => setMode("avatar")} data-testid="video-mode-avatar"><User size={14} /> Stock presenter</button>
            <button className={`sr-btn sr-btn-sm ${mode === "photo" ? "sr-btn-primary" : "sr-btn-ghost"}`}
              onClick={() => setMode("photo")} data-testid="video-mode-photo"><Upload size={14} /> Your photo</button>
          </div>

          {mode === "avatar" ? (
            <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", gap: 10 }}>
              {opts.avatars.map((a) => (
                <button key={a.avatar_id} onClick={() => setAvatarId(a.avatar_id)} data-testid={`avatar-${a.avatar_id}`}
                  style={{ padding: 0, borderRadius: 12, overflow: "hidden", cursor: "pointer", background: "#fff",
                    border: avatarId === a.avatar_id ? "2px solid var(--blue)" : "1px solid var(--border)" }}>
                  {a.preview ? <img src={a.preview} alt={a.name} style={{ width: "100%", height: 110, objectFit: "cover", display: "block" }} />
                    : <div style={{ height: 110, background: "var(--blue-light)" }} />}
                  <div style={{ fontSize: 11, padding: "6px 4px", color: "var(--navy)", fontWeight: 600, textAlign: "center" }}>{(a.name || "").split(" ").slice(0, 2).join(" ")}</div>
                </button>
              ))}
            </div>
          ) : (
            <div data-testid="video-photo-upload">
              {presenters.length > 0 && (
                <div style={{ marginBottom: 14 }} data-testid="presenter-library">
                  <p className="sr-muted" style={{ fontSize: 12, marginBottom: 8 }}>Your saved presenters — tap to reuse:</p>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {presenters.map((p) => (
                      <div key={p.id} style={{ position: "relative" }}>
                        <button onClick={() => setPhoto({ talking_photo_id: p.talking_photo_id, preview_url: p.preview_url })}
                          data-testid={`presenter-${p.id}`}
                          style={{ padding: 0, borderRadius: 12, overflow: "hidden", cursor: "pointer", background: "#fff",
                            border: photo?.talking_photo_id === p.talking_photo_id ? "2px solid var(--blue)" : "1px solid var(--border)" }}>
                          <img src={p.preview_url} alt={p.label} style={{ width: 84, height: 84, objectFit: "cover", display: "block" }} />
                          <div style={{ fontSize: 10, padding: "4px 2px", color: "var(--navy)", fontWeight: 600, textAlign: "center" }}>{p.label}</div>
                        </button>
                        <button onClick={() => removePresenter(p.id)} data-testid={`presenter-remove-${p.id}`} aria-label="Remove"
                          style={{ position: "absolute", top: -6, right: -6, background: "#fff", border: "1px solid var(--border)", borderRadius: 999, width: 22, height: 22, cursor: "pointer", color: "var(--danger,#dc2626)", lineHeight: 1 }}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {photo ? (
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <img src={photo.preview_url} alt="Your presenter" style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 12, border: "2px solid var(--blue)" }} data-testid="video-photo-preview" />
                  <div>
                    <p style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600 }}>Photo ready</p>
                    <p className="sr-muted" style={{ fontSize: 13 }}>It&apos;ll be animated and lip-synced to your voiceover.</p>
                    <label className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 8, cursor: "pointer" }}>
                      Upload another
                      <input type="file" accept="image/png,image/jpeg" onChange={uploadPhoto} style={{ display: "none" }} />
                    </label>
                  </div>
                </div>
              ) : (
                <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
                  border: "2px dashed var(--border)", borderRadius: 14, padding: "28px 16px", cursor: "pointer", textAlign: "center" }}>
                  {uploading ? <Loader2 size={22} className="sr-spin" style={{ color: "var(--blue)" }} /> : <Upload size={22} color="var(--blue)" />}
                  <span style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600 }}>{uploading ? "Preparing your photo…" : "Upload a clear, front-facing photo"}</span>
                  <span className="sr-muted" style={{ fontSize: 12 }}>JPG or PNG · a person&apos;s face works best</span>
                  <input type="file" accept="image/png,image/jpeg" onChange={uploadPhoto} style={{ display: "none" }} data-testid="video-photo-input" />
                </label>
              )}
            </div>
          )}
        </div>

        <button className="sr-btn sr-btn-primary" style={{ marginTop: 16 }} onClick={generate} disabled={busy || (active && active.status === "processing")} data-testid="video-generate">
          {busy ? <><Loader2 size={16} className="sr-spin" /> Starting…</> : <><Clapperboard size={16} /> Generate video ad</>}
        </button>
      </div>

      {/* Active render */}
      {active && (
        <div className="sr-card" style={{ marginTop: 16 }} data-testid="video-active">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            {active.status === "processing" && <><Loader2 size={18} className="sr-spin" style={{ color: "var(--blue)" }} /> <strong>Rendering your commercial…</strong></>}
            {active.status === "completed" && <><CheckCircle2 size={18} style={{ color: "var(--success)" }} /> <strong>Your video ad is ready</strong></>}
            {active.status === "failed" && <><XCircle size={18} style={{ color: "var(--danger)" }} /> <strong>Rendering failed</strong></>}
          </div>
          {active.status === "processing" && <p className="sr-muted" style={{ fontSize: 13 }}>ElevenLabs voiceover + HeyGen presenter. This usually takes about a minute — you can keep working.</p>}
          {active.status === "completed" && active.video_url && (
            <div>
              <video src={active.video_url} controls style={{ width: "100%", maxWidth: 560, borderRadius: 12, border: "1px solid var(--border)" }} data-testid="video-player" />
              <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <a href={active.video_url} download className="sr-btn sr-btn-ghost sr-btn-sm"><Download size={14} /> Download MP4</a>
                {active.audio_url && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => playVoice(active.audio_url)}><Play size={14} /> Play voiceover</button>}
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => openRefine(active)} data-testid={`video-refine-${active.id}`}><Sparkles size={14} /> Refine with AI</button>
              </div>
            </div>
          )}
          {active.status === "failed" && <p className="sr-muted" style={{ fontSize: 13 }}>{active.error || "Try a different presenter or a shorter script."}</p>}
        </div>
      )}

      {/* Library */}
      {videos.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Your video ads</div>
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))" }}>
            {videos.map((v) => (
              <div key={v.id} className="sr-card" data-testid={`video-${v.id}`}>
                <strong style={{ color: "var(--navy)", fontSize: 14 }}>{v.title}</strong>
                <span className={`sr-badge ${v.status === "completed" ? "sr-badge-green" : v.status === "failed" ? "sr-badge-gray" : "sr-badge-amber"}`} style={{ marginLeft: 8 }}>{v.status}</span>
                {v.status === "completed" && v.video_url && <video src={v.video_url} controls style={{ width: "100%", borderRadius: 10, marginTop: 8, border: "1px solid var(--border)" }} />}
                <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>{v.script}</p>
                {v.status === "completed" && v.video_url && (
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 8, width: "100%" }} onClick={() => openRefine(v)} data-testid={`video-refine-${v.id}`}>
                    <Sparkles size={13} /> Refine with AI
                  </button>
                )}
                {v.status === "completed" && v.refined_from && (
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 6, width: "100%" }} data-testid={`video-compare-${v.id}`}
                    onClick={() => { const orig = videos.find((x) => x.id === v.refined_from); orig ? setCompare({ refined: v, original: orig }) : toast.error("Original video is no longer available"); }}>
                    <GitCompare size={13} /> Compare to original
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {compare && (
        <div className="sr-modal-overlay" onClick={() => setCompare(null)} data-testid="video-compare-modal"
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20 }}>
          <div className="sr-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <strong style={{ fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}><GitCompare size={18} style={{ color: "var(--blue)" }} /> Original vs. AI-refined</strong>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setCompare(null)} data-testid="video-compare-close">Close</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16 }}>
              {[["Original", compare.original], ["AI-refined", compare.refined]].map(([label, v]) => (
                <div key={label} data-testid={`compare-${label === "Original" ? "original" : "refined"}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span className={`sr-badge ${label === "Original" ? "sr-badge-gray" : "sr-badge-green"}`}>{label}</span>
                    <strong style={{ fontSize: 13, color: "var(--navy)" }}>{v.title}</strong>
                  </div>
                  {v.video_url ? <video src={v.video_url} controls style={{ width: "100%", borderRadius: 10, border: "1px solid var(--border)" }} />
                    : <div className="sr-muted" style={{ fontSize: 12 }}>Video not available</div>}
                  <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>{v.script}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {refine && (
        <RefineChat
          title="Refine this video ad"
          hint="Tell me how to improve it — e.g. “make it shorter and punchier”, “add a limited-time offer”, or “friendlier, more upbeat tone”. I'll rewrite the script and re-render the video."
          placeholder="Describe the change…"
          thread={refine.thread}
          busy={refine.busy}
          preview={
            <div style={{ background: "#F7FAFF", border: "1px solid var(--border)", borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 13 }}>{refine.video.title}</div>
              <div className="sr-muted" style={{ fontSize: 12, marginTop: 2 }}>{refine.video.script}</div>
            </div>
          }
          onSend={sendVideoRefine}
          onClose={() => setRefine(null)}
        />
      )}
    </div>
  );
}

const Head = () => (
  <div style={{ marginBottom: 20 }}>
    <span className="sr-badge" style={{ marginBottom: 8 }}><Clapperboard size={12} /> AI Video Ad Studio</span>
    <h1 style={{ fontSize: 30 }}>Turn a script into a finished commercial</h1>
    <p className="sr-muted" style={{ marginTop: 6 }}>Pick a presenter and voice, add your script, and AI produces a ready-to-run video ad — voiceover and on-screen presenter included.</p>
  </div>
);
